import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableDataSource } from '@angular/material/table';
import { SpecialCharacters } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin/user-admin.model';
import { Items } from 'src/app/core/models/common/items.model';
import { TableColumn } from 'src/app/core/models/common/table-column.model';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { UserProfilePermissionUtils } from 'src/app/core/utils/permissions/user-profile-permissions/user-profile-permissions.utils';
import { ProfileModalComponent } from '../../modal/profile-modal/profile-modal.component';
import { FORM_STATUS } from '../../modal/profile-modal/profile-modal.constants';
import { BUTTON_CLICK } from '../../modal/warning-modal/warning-modal.constants';
import { ERROR_TEXT, IMAGE_SRC, STATUS_OPTIONS } from '../../shared-constants';
import {
  ColumnIds,
  ColumnText,
  CONSTS,
  EXCEL_FILE_NAME,
  GLOBAL_ID_FILE_NAME,
} from './user-admin-table.constants';

/** UserAdminTableComponent
 *
 * Responsible for rendering and managing the Admin Search table.
 *
 * Features:
 * - Displays user administrative data with configurable columns
 * - Supports column-level searching, filtering, and pagination
 * - Enables role-based actions (view, add, edit, delete users)
 * - Integrates status and role dropdown filters
 * - Exports filtered results to Excel
 *
 * Behavior:
 * - Data is fetched based on current user role and location
 * - Filters are applied client-side using MatTableDataSource
 * - All subscriptions are automatically cleaned up using DestroyRef
 */
@Component({
  selector: 'app-user-admin-table',
  templateUrl: './user-admin-table.component.html',
  styleUrls: ['./user-admin-table.component.scss'],
  standalone: false,
})
export class UserAdminTableComponent implements OnInit, AfterViewInit {
  @Input() radioHeader!: string;
  @Input() firstOptionValue!: string;
  @Input() firstOptionName!: string;
  @Input() secondOptionValue!: string;
  @Input() secondOptionName!: string;
  @Input() headerText = CONSTS.WELCOME_TEXT;
  @Input() hasRadioButton = false;
  @Input() radioModel!: string;
  @Input() isAdminSearch = false;
  @Output() radioButtonChange = new EventEmitter<Event>();

  private userService = inject(UserService);
  private destroy = inject(DestroyRef);

  readonly displayedColumns = Object.values(ColumnIds);
  readonly COLUMNTEXT = ColumnText;
  readonly STATUS_OPTIONS = STATUS_OPTIONS;
  readonly IMAGE = IMAGE_SRC;
  readonly ERROR_TEXT = ERROR_TEXT;
  readonly SELECT_ALL = STATUS_OPTIONS.ALL_SELECTED;
  readonly CONSTS = CONSTS;
  readonly CHARS = SpecialCharacters;

  // Create column search objects
  createTableColumn(columnId: string): TableColumn {
    return {
      itemId: columnId,
      ids: [],
      formControl: new FormControl(''),
      checkAllIds: true,
    };
  }

  // Create admin table columns
  userIdData = this.createTableColumn(ColumnIds.USER_ID);
  employeeIdData = this.createTableColumn(ColumnIds.EMPLOYEE_ID);
  lastNameData = this.createTableColumn(ColumnIds.LAST_NAME);
  firstNameData = this.createTableColumn(ColumnIds.FIRST_NAME);
  locationGroupData = this.createTableColumn(ColumnIds.LOCATION_GROUP);
  locationData = this.createTableColumn(ColumnIds.LOCATION);
  userRoleData = this.createTableColumn(ColumnIds.USER_ROLE);
  emailData = this.createTableColumn(ColumnIds.EMAIL);
  lastLoginData = this.createTableColumn(ColumnIds.LAST_LOGIN);
  statusData = this.createTableColumn(ColumnIds.STATUS);

  //Misc
  selectedUser!: UserAdmin;
  dataSource = new MatTableDataSource<UserAdmin>();
  userData: UserAdmin[] = [];
  currentUser?: UserAdmin;
  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  roles: Items[] = [];
  dropdownOpen = [false, false];
  currRole = '';

  constructor(
    readonly dialog: MatDialog,
    public commonUtils: CommonUtils,
    private roleUtils: UserProfilePermissionUtils,
  ) {}

  /** Angular lifecycle hook invoked during component initialization.
   *
   * This method:
   * - Subscribes to the current logged-in user stream
   * - Initializes role-based permissions
   * - Loads user data based on admin search mode and location
   * - Fetches available roles for role-based filtering
   *
   * Subscription cleanup is handled automatically using DestroyRef.
   */
  ngOnInit(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((user): user is UserAdmin => !!user),
        tap((user) => {
          this.currentUser = user;
          this.currRole = user.roleName;
          this.refreshData(this.currentUser?.locationCode);
          this.getRoles();
        }),
      )
      .subscribe();
  }

  /** Toggles the global loading modal used across the application.
   *
   * This method is typically invoked before and after
   * asynchronous operations to indicate processing state
   * and prevent unintended user interaction.
   *
   * @param flag - Determines whether the loading modal is shown or hidden
   */
  toggleLoading(flag: boolean): void {
    this.commonUtils.updateLoadingModal(flag);
    this.commonUtils.lockUpdateLoadingModal(flag);
  }

  /** Retrieves all available user roles from the backend service.
   *
   * This method:
   * - Populates the role dropdown filter options
   * - Initializes all roles as selected by default
   * - Stores role data centrally via the UserService
   *
   * Roles are later used for both filtering and permission checks.
   */
  getRoles() {
    this.userService
      .getAllRoles()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((res) => {
          // Add to User Role Dropdown
          res.response?.roles?.forEach((role) => {
            this.roles.push({
              id: role.roleName,
              selected: true,
            });
          });
          this.userService.setUserRoles$(res.response.roles);
        }),
      )
      .subscribe();
  }

  /** Retrieves all available user roles from the backend service.
   *
   * This method:
   * - Populates the role dropdown filter options
   * - Initializes all roles as selected by default
   * - Stores role data centrally via the UserService
   *
   * Roles are later used for both filtering and permission checks.
   */
  ngAfterViewInit() {
    this.dataSource.filterPredicate = this.commonUtils.createFilter();
  }

  /** Exports the current table data to an Excel file.
   *
   * This method performs the following steps:
   * - Extracts only the rows currently visible in the table
   *   (i.e., data after all active filters are applied).
   * - Maps the table data to a flattened export-friendly structure,
   *   including only the required user attributes.
   * - Delegates the actual Excel file generation and download
   *   to the CommonUtils utility service.
   *
   * Behavior:
   * - The exported file reflects the current filter state of the table.
   * - Column headers are derived from the ColumnText configuration.
   * - The file name is dynamically selected based on admin search mode.*/
  exportExcel(): void {
    const filteredData = this.dataSource.filteredData.map((item) => ({
      loginId: item.loginId,
      employeeId: item.employeeId,
      familyName: item.familyName,
      givenName: item.givenName,
      locationCode: item.locationCode,
      locationGroupCode: item.locationGroupCode,
      roleName: item.roleName,
      email: item.email,
      lastLoggedIn: item.lastLoggedIn,
      enabled: item.enabled,
    }));
    this.commonUtils.exportExcel(
      Object.values(ColumnText),
      this.isAdminSearch ? GLOBAL_ID_FILE_NAME : EXCEL_FILE_NAME,
      filteredData,
    );
  }

  /** Deletes the specified user from the backend and updates the UI.
   *
   * Responsibilities:
   * - Invokes backend delete operation
   * - Removes user from the table data source
   * - Displays a success banner upon completion
   *
   * @param selectedUser User to be removed
   */
  removeUserFromList(selectedUser: UserAdmin) {
    const selectedId = selectedUser?.loginId || '';
    if (selectedId) {
      this.userService
        .deleteUser(selectedId)
        .pipe(take(1))
        .subscribe(() => {
          this.refreshData(this.currentUser?.locationCode);
          const filteredData = this.dataSource.data.filter(
            (ele: UserAdmin) =>
              ele?.loginId?.toLowerCase() !=
              selectedUser?.loginId.toLowerCase(),
          );
          this.dataSource.data = filteredData;
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        });
    }
  }

  /** Clears all active filters and restores the original table state.
   *
   * Actions performed:
   * - Resets all column form controls
   * - Clears dropdown selections
   * - Restores cached user data
   * - Triggers dropdown reset events
   */
  clearAllFilters() {
    this.commonUtils.clearAllFilters(this.dataSource, this.userData, [
      this.userIdData.formControl,
      this.employeeIdData.formControl,
      this.lastNameData.formControl,
      this.firstNameData.formControl,
      this.emailData.formControl,
      this.locationGroupData.formControl,
      this.locationData.formControl,
      this.lastLoginData.formControl,
    ]);

    this.resetSelectDropdowns.next(true);
  }

  /** Applies all active filters to the table.
   *
   * Combines:
   * - Column-level text filters
   * - Dropdown-based role and status filters
   *
   * Filtering is performed client-side using a custom predicate
   * applied to the MatTableDataSource.
   */
  search() {
    this.commonUtils.searchFilter(
      [
        this.userIdData,
        this.employeeIdData,
        this.lastNameData,
        this.firstNameData,
        this.locationGroupData,
        this.locationData,
        this.emailData,
        this.lastLoginData,
      ],
      this.dataSource,
      [this.statusData, this.userRoleData],
    );
  }

  /**
   * Handles changes to dropdown-based filters (status or role).
   *
   * Updates the selected filter value for the given column
   * and reapplies the table filtering logic.
   *
   * @param $event Selected dropdown value
   * @param field Associated TableColumn configuration
   */
  onStatusChangeForDropdown($event: unknown, field: TableColumn) {
    field.ids[0] = $event as Items;
    this.search();
  }

  /**
   * Opens the Add User modal dialog.
   *
   * On successful user creation:
   * - Reloads the user list
   * - Displays a success banner
   *
   * @param user Optional user data (not typically used for add)
   */
  addUser(user?: UserAdmin) {
    const dialogRef = this.dialog.open(ProfileModalComponent, {
      width: '47.5rem',
      disableClose: true,
      data: {
        data: user ? user : null,
        formStatus: FORM_STATUS.ADD,
        disableEmailRole: true,
        modalHeader: CONSTS.ADD_USER_HEADER,
        isOwnProfile: false,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((status) => {
        if (status) {
          this.refreshData(this.currentUser?.locationCode);
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
        }
      });
  }

  /**
   * Opens the View User modal dialog in read-only mode.
   *
   * Displays full user details without allowing modifications.
   *
   * @param user User whose details are to be viewed
   */
  viewUser(user: UserAdmin) {
    const _userSnapshot = JSON.parse(JSON.stringify(user));
    this.dialog.open(ProfileModalComponent, {
      width: '47.5rem',
      disableClose: true,
      data: {
        data: _userSnapshot,
        formStatus: FORM_STATUS.VIEW,
        disableEmailRole: true,
        showButton: false,
        modalHeader: CONSTS.VIEW_USER_HEADER,
        isOwnProfile: false,
      },
    });
  }

  /** Opens the Edit User modal dialog.
   *
   * On successful update:
   * - Reloads the user list
   * - Displays a success banner
   *
   * @param user User to be edited
   */
  editUser(user: UserAdmin) {
    if (user) {
      const _userSnapshot = JSON.parse(JSON.stringify(user));
      const dialogRef = this.dialog.open(ProfileModalComponent, {
        width: '47.5rem',
        disableClose: true,
        data: {
          data: _userSnapshot,
          formStatus: FORM_STATUS.EDIT,
          disableEmailRole: true,
          modalHeader: CONSTS.EDIT_USER_HEADER,
          isOwnProfile: false,
        },
      });
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((status) => {
          if (status) {
            this.refreshData(this.currentUser?.locationCode);
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
          }
        });
    }
  }

  /**
   * Initiates the user deletion workflow.
   *
   * Displays a confirmation modal and proceeds with deletion
   * only after explicit user approval.
   *
   * @param admin User selected for deletion
   */
  removeUser(admin: UserAdmin) {
    this.commonUtils
      .openWarningModal({
        header: this.CONSTS.REMOVE_USER_HEADER,
        body: this.CONSTS.REMOVE_USER_TEXT,
        extraText: `${admin?.givenName} ${admin?.familyName}`,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.removeUserFromList(admin);
        }
      });
  }

  /**
   * Emits radio button selection changes to the parent component.
   * Used to notify parent components of admin search mode changes.
   * @param $event Radio button change event
   */
  emitRadioButtonChange($event: Event) {
    this.radioButtonChange.emit($event);
  }

  /** Opens the Add User modal dialog.
   * On successful user creation:
   * - Reloads the user list
   * - Displays a success banner
   *
   * @param user Optional user data (not typically used for add)
   */
  get canAddUsers() {
    return this.roleUtils.canAddUsers(this.currRole);
  }

  /** Opens the Edit User modal dialog.
   *
   * On successful update:
   * - Reloads the user list
   * - Displays a success banner
   *
   * @param user User to be edited
   */
  get canEditUsers() {
    return this.roleUtils.canEditUsers(this.currRole);
  }

  /* Initiates the user deletion workflow.
   *
   * Displays a confirmation modal and proceeds with deletion
   * only after explicit user approval.
   *
   * @param admin User selected for deletion
   */
  get canDeleteUsers() {
    return this.roleUtils.canDeleteUsers(this.currRole);
  }

  /**
   * Indicates whether any filter is currently active.
   *
   * Used by the UI to enable or disable the "Clear Filters" action.
   * @returns True if at least one filter is applied, otherwise false
   */
  get isFilterEnabled() {
    return (
      this.userIdData.formControl.value !== '' ||
      this.employeeIdData.formControl.value !== '' ||
      this.lastNameData.formControl.value !== '' ||
      this.firstNameData.formControl.value !== '' ||
      this.locationData.formControl.value !== '' ||
      this.locationGroupData.formControl.value !== '' ||
      this.emailData.formControl.value !== '' ||
      this.lastLoginData.formControl.value !== '' ||
      this.userRoleData.ids.length > 0 ||
      this.statusData.ids.length > 0
    );
  }

  /** Resets filter state and reloads user data from the backend.
   *
   * Responsibilities:
   * - Clears all filters and cached data
   * - Determines whether to fetch global or location-specific users
   * - Triggers a fresh user data retrieval
   *
   * @param locationCode Optional location code for scoped searches
   */
  private refreshData(locationCode?: string) {
    this.clearAllFilters();
    this.userData = [];
    this.userIdData.ids = [];
    this.employeeIdData.ids = [];
    this.lastNameData.ids = [];
    this.firstNameData.ids = [];
    this.emailData.ids = [];
    this.locationData.ids = [];
    this.locationGroupData.ids = [];
    this.userRoleData.ids = [];

    // get all users from every group if true
    // otherwise get by current selected location
    this.isAdminSearch ? this.getAllUsers() : this.getAllUsers(locationCode);
  }

  /** Retrieves user data from the backend service.
   *
   * Responsibilities:
   * - Fetches users based on admin mode and location
   * - Sorts users alphabetically by last name
   * - Initializes the table data source
   * - Caches data for filter reset operations
   *
   * @param locationCode Optional location filter
   */
  getAllUsers(locationCode?: string): void {
    this.userService
      .getAllUsers(locationCode, this.isAdminSearch)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((bundled) => {
        const users = bundled?.response?.users;
        if (users && users.length > 0) {
          users.sort((a, b) =>
            (a.familyName?.toString() || '').localeCompare(
              b.familyName?.toString() || '',
            ),
          );
          this.dataSource.data = users;
          users.forEach((data) => {
            this.userData.push(data);
          });
        } else {
          this.dataSource.data = [];
          this.userData = [];
        }
      });
  }
}
